// @flow
import React, { Component, Fragment } from 'react';
import update from 'immutability-helper';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import CountDown from '../../../../../elements/CountDown/CountDown';
import { FormattedMessage } from 'react-intl';
import Loading from '../../../../../elements/Loading/Loading';
import UploadBox from '../../components/UploadBox/UploadBox';
import Modal from '../../../../../elements/Modal/Modal';
import { animateScroll as scroll } from 'react-scroll/modules';
import ReportModalContainer from '../../../../report/container/ReportModalContainer';
import FloatingBottom from '../../../../../elements/FloatingBottom/FloatingBottom'

import commonStyle from '../../../components/OrderCommon.scss';
import boxStyle from '../../../../../assets/styles/box.scss';
import buttonStyle from '../../../../../assets/styles/button.scss';
import style from './Reserved.scss';

import type { Node } from 'react';
import type { Props as ProductProps } from '../../../../../elements/Product/Product';
import type { BagItem, Order, OrderItem, ShoppingCartItem } from '../../../../../entities';
import type { HTTPError } from '../../../../../error';
import type { Motive, MotiveKey } from '../../../../report/duck';
import type { LocaleState } from '../../../../locale/duck';

type Props = {
  +buildProducts: (renderContent?: (props: ProductProps) => Node) => Node;
  +buildSummary: () => Node;
  +order: Order;
  +itemLoadingMap: { [key: string]: boolean };
  +itemErrorMap: { [key: string]: HTTPError };
  +isProgressing?: boolean;
  +progressError?: HTTPError;
  +uploadInvoiceFile: (orderId: string, itemNumber: string, imageData: FormData, ext: string) => void;
  +progressPurchase: (orderId: string) => void;
  +cancelReservation: (orderId: string, flightId: string) => void;
  +isCanceling?: boolean;
  +cancelError?: HTTPError;
  +motives?: { [key: MotiveKey]: Motive[] };
  +locale: LocaleState;
  +goToRedirect: (string) => void;
};

type State = {
  formatErrorMap: {
    [key: string]: boolean;
  };
  cancelModalOpen: boolean;
  reportModalVisible: boolean;
};

type Item = OrderItem | BagItem | ShoppingCartItem;

const validFormats = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];

export default class Reserved extends Component<Props, State> {
  state = {
    formatErrorMap: {},
    cancelModalOpen: false,
    reportModalVisible: false,
  };

  componentDidUpdate(prevProps: Props) {
    const { cancelError } = this.props;
    if (!prevProps.cancelError && !!cancelError) {
      this.scrollAnimateTop();
      this.setState({ cancelModalOpen: false });
    }
  }

  scrollAnimateTop = () =>
    scroll.scrollTo(0, {
      duration: 400,
    });


  handleRejectedFile(rejectedFiles: File[], item: Item) {
    if (rejectedFiles.length) {
      if (rejectedFiles[0].type === '' || !validFormats.includes(rejectedFiles[0].type) ||
        rejectedFiles[0].size > 3145728) {
        this.setState((state: State) => update(state, {
          formatErrorMap: {
            [`${item.number}`]: { $set: true },
          },
        }));
      }
    }
  }

  getFilePathExtension = (path: string) => {
    let filename = path.split('\\').pop().split('/').pop();
    return filename.substr((Math.max(0, filename.lastIndexOf('.')) || Infinity) + 1);
  };

  handleChangeFile(acceptedFiles: File[], item: Item) {
    if (acceptedFiles.length) {
      const { uploadInvoiceFile, order } = this.props;
      const ext = this.getFilePathExtension(acceptedFiles[0].name);
      // Only if we wanna see the miniature
      /*const reader = new FileReader();
      reader.onload = () => {
        const fileAsDataURL = reader.result;
        console.log('finished loading');
      };
      reader.onabort = () => console.log('file reading was aborted');
      reader.onerror = () => console.log('file reading has failed');
      reader.readAsDataURL(acceptedFiles[0]);*/
      var formData = new FormData();
      formData.append('file', acceptedFiles[0]);
      formData.append('resourceGroup', 'EXTERNAL_INVOICE');
      uploadInvoiceFile(order.id, item.number, formData, ext);
    }
  }

  renderContent = (props: ProductProps): Node => {
    const { formatErrorMap } = this.state;
    const { itemLoadingMap, itemErrorMap } = this.props;
    const { item } = props;
    return (
      <section className={style.uploadInvoice}>
        <FormattedMessage id="order.sale.reserved.product.uploadInvoice.title">{
          txt => <p className={style.title}>{txt}</p>
        }</FormattedMessage>
        {itemLoadingMap[`${item.number}`] &&
        <article className={style.loadingWrap}>
          <FormattedMessage id="order.sale.reserved.product.uploadInvoice.loading">{
            txt => <p className={style.text}>{txt}</p>
          }</FormattedMessage>
          <Loading type="progress-bar"
                   color="orange"
                   className={style.progressBar}/>
        </article>}
        {!itemLoadingMap[`${item.number}`] &&
        <UploadBox isLoading={itemLoadingMap[`${item.number}`]}
                   error={!!itemErrorMap[`${item.number}`] || formatErrorMap[`${item.number}`]}
                   hasInvoice={!!(item: OrderItem).externalInvoice}
                   uploadedIntl="order.sale.reserved.product.uploadInvoice.uploaded"
                   uploadIntl="order.sale.reserved.product.uploadInvoice.button"
                   handleRejectedFile={(files: File[]) => this.handleRejectedFile(files, item)}
                   handleChangeFile={(files: File[]) => this.handleChangeFile(files, item)}
        />}
      </section>
    );
  };

  isButtonDisabled = (): boolean => {
    const { order } = this.props;
    for (let i = 0; i < order.items.length; i++) {
      if (!order.items[i].externalInvoice && (order.items[i].cancelledCount !== order.items[i].amount)) {
        return true;
      }
    }
    return false;
  };

  handleCloseReportModal = () => {
    this.setState({ reportModalVisible: false });
  };

  handleOpenReportModal = () => {
    this.setState({ reportModalVisible: true });
  };

  render() {
    const {
      buildProducts, buildSummary, order, progressPurchase, isCanceling, cancelReservation, motives,
      locale, goToRedirect,
    } = this.props;
    const { cancelModalOpen, reportModalVisible } = this.state;
    const options = motives ? motives.traveler.map((motive: Motive) => ({
      value: motive.name,
      label: motive.translations[locale.locale.toUpperCase()],
    })) : [];
    return (
      <Fragment>
        <main className={commonStyle.mainWrap}>
          <section className={classNames(commonStyle.wrap, commonStyle.leftWrap)}>
            <section className={classNames(boxStyle.box, style.reservationTimeSection)}>
              <FormattedMessage id="reserve.time.limit.title">{
                txt => <h2 className={classNames(boxStyle.boxTitle, boxStyle.smallSpace)}>{txt}</h2>
              }</FormattedMessage>
              <div className={style.reservationTimer}>
                <CountDown expiresAt={order.reservationExpiresAt}
                           handleTimeUp={() => goToRedirect(`/flight/${order.flight.id}/requests`)}/>
              </div>
              <FormattedMessage id="reserve.time.limit.message">{
                txt => <p className={classNames(boxStyle.subtitle, boxStyle.noSpace)}>{txt}</p>
              }</FormattedMessage>
            </section>
            {buildProducts(this.renderContent)}
          </section>
          <section className={classNames(commonStyle.wrap, commonStyle.rightWrap)}>
            {buildSummary()}
            <FormattedMessage id="order.sale.reserved.continue.button">{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.orange, style.continue)}
                             disabled={this.isButtonDisabled()}
                             onClick={_ => progressPurchase(order.id)}>{txt}</button>
            }</FormattedMessage>
            <FormattedMessage id="order.sale.reserved.cancel.button">{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.red, style.cancel)}
                             onClick={_ => this.setState({ cancelModalOpen: true })}>{txt}</button>
            }</FormattedMessage>
            <FormattedMessage id="order.sale.reserved.report.button">{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.redInverse, style.report)}
                             onClick={this.handleOpenReportModal}>{txt}</button>
            }</FormattedMessage>
          </section>
          <section className={style.buttonsWrap}>
            <FloatingBottom offSetY={216}>
              <FormattedMessage id="order.sale.reserved.continue.button">{
                txt => <button className={classNames(buttonStyle.btn, buttonStyle.orange)}
                               disabled={this.isButtonDisabled()}
                               onClick={_ => progressPurchase(order.id)}>{txt}</button>
              }</FormattedMessage>
              <FormattedMessage id="order.sale.reserved.cancel.button">{
                txt => <button className={classNames(buttonStyle.btn, buttonStyle.red, style.below)}
                               onClick={_ => this.setState({ cancelModalOpen: true })}>{txt}</button>
              }</FormattedMessage>
              <FormattedMessage id="order.sale.reserved.report.button">{
                txt => <button className={classNames(buttonStyle.btn, buttonStyle.redInverse, style.below)}
                               onClick={this.handleOpenReportModal}>{txt}</button>
              }</FormattedMessage>
            </FloatingBottom>
          </section>
        </main>
        <Modal handleClose={() => !isCanceling && this.setState({ cancelModalOpen: false })}
               width={544}
               height={616}
               open={cancelModalOpen}>
          {isCanceling && <Loading type="circle"
                                   color="lightBlue"
                                   className={style.modalLoading}/>}
          {!isCanceling &&
          <section className={style.cancelOrderModal}>
            <FormattedMessage id="order.sale.reserved.modal.cancel.title">{
              text => <h2 className={style.title}>{text}</h2>
            }</FormattedMessage>
            <FormattedMessage id="order.sale.reserved.modal.cancel.text">{
              txt => <p>{txt}</p>
            }</FormattedMessage>
            <FormattedMessage id="order.sale.reserved.modal.cancel.button.cancel">{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.red, style.btn)}
                             onClick={() => cancelReservation(order.id, order.flight.id)}>{txt}</button>
            }</FormattedMessage>
            <FormattedMessage id="order.sale.reserved.modal.cancel.button.close">{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.darkGrey, style.btn)}
                             onClick={() => this.setState({ cancelModalOpen: false })}>{txt}</button>
            }</FormattedMessage>
          </section>}

        </Modal>
        <ReportModalContainer color="orange"
                              reportType="TRAVELER_TRACKING"
                              isOpen={reportModalVisible}
                              handleClose={this.handleCloseReportModal}
                              isDiy={order.isDiy}
                              id={order.id}
                              options={options}
                              successIntl="modal.report.traveler.success.text"/>
      </Fragment>
    );
  }
}

Reserved.propTypes = {
  buildProducts: PropTypes.func.isRequired,
  buildSummary: PropTypes.func.isRequired,
  order: PropTypes.object.isRequired,
  itemLoadingMap: PropTypes.object.isRequired,
  itemErrorMap: PropTypes.object.isRequired,
  isProgressing: PropTypes.bool,
  progressError: PropTypes.object,
  uploadInvoiceFile: PropTypes.func.isRequired,
  progressPurchase: PropTypes.func.isRequired,
  cancelReservation: PropTypes.func.isRequired,
  isCanceling: PropTypes.bool,
  cancelError: PropTypes.object,
  motives: PropTypes.object,
  locale: PropTypes.object.isRequired,
  goToRedirect: PropTypes.func.isRequired,
};



// WEBPACK FOOTER //
// ./src/modules/order/Sale/reserved/components/Reserved.jsx